import { PermissionsKey } from '@vedalib/crud'
import { useDebounceFn } from 'ahooks'
import { useCallback, useMemo } from 'react'
import { useParams } from 'react-router'

import { sendTypedEvent } from 'analytics'
import Form from 'components/controls/Form'
import FormControls from 'components/controls/FormControls'
import FormField from 'components/controls/FormField'
import type { MCWithParams } from 'components/uiKit/Modal'
import Modal, { ModalBody, ModalFooter } from 'components/uiKit/Modal'
import { notify } from 'components/uiKit/Notification'
import { NotificationType } from 'components/uiKit/Notification/types'
import { Typography } from 'components/uiKit/Typography'
import {
  useTemplatesCreate,
  useTemplatesGetGroup,
  useTemplateTagsByCollectionLazy,
} from 'gql/templates/apollo'
import { usePermissions } from 'services/Permission/PermissionContext'
import type { SectionTypeEnum } from 'services/Store/Project/enums'
import type { Block } from 'services/Store/Project/types'
import { t } from 'services/Translation'
import type { CompanyParams } from 'store/models'

import { nameField, createdForField, templateTagIds, InputErrorValues } from './fieldConfigs'

interface ITemplateSaveValues {
  name: string
  templateTagIds?: string[]
  editorTemplateGroupId?: string
}

export interface IParams {
  block?: Block
  blocks?: Block[]
  sectionType: SectionTypeEnum
  defaultTag?: string
}

const INFO_INDENTS = { marginTop: '4px' }

const getAllowedCollectionTypes = (permissions: ReturnType<typeof usePermissions>) => {
  const allowedCollectionTypes = ['project']

  if (permissions.has(PermissionsKey.company_collections_company_u)) {
    allowedCollectionTypes.push('company')
  }

  if (permissions.has(PermissionsKey.company_collections_self)) {
    allowedCollectionTypes.push('employee')
  }

  return allowedCollectionTypes
}

const TemplateSaveModal: MCWithParams<IParams> = ({
  onClose,
  params: { block, blocks, defaultTag },
}) => {
  const { projectId, companyId } = useParams<CompanyParams & { projectId: string }>()

  const permissions = usePermissions()
  const allowedCollectionTypes = useMemo(
    () => getAllowedCollectionTypes(permissions),
    [permissions],
  )

  const { data } = useTemplatesGetGroup({ companyId, data: { projectId } })

  const [getCollectionTags, { data: templateTagsData, loading }] = useTemplateTagsByCollectionLazy()

  const templateTags = useMemo(
    () => templateTagsData?.data.templateTags || [],
    [templateTagsData?.data.templateTags],
  )

  const collections = useMemo(
    () => [
      ...(data?.data.templateCollections || [])
        .filter((collection) => allowedCollectionTypes.includes(collection.createdFor))
        .map((group) => ({ value: group.id, label: group.name })),
    ],
    [data?.data?.templateCollections, allowedCollectionTypes],
  )

  const isMultiple = !!blocks && blocks.length > 0
  const blockName = isMultiple ? t('modal.saveTemplate.combinedTemplateName') : block?.name || ''

  const initialFormValues = useMemo<ITemplateSaveValues>(
    () => ({ name: blockName, templateTagIds: defaultTag ? [defaultTag] : [] }),
    [blockName, defaultTag],
  )

  const [createTemplate] = useTemplatesCreate()

  const onChangeValues = useDebounceFn(
    async (values: ITemplateSaveValues) => {
      const collectionId = values?.editorTemplateGroupId
      if (collectionId) {
        await getCollectionTags({ variables: { collectionId, companyId } })
      }
    },
    { wait: 500 },
  ).run

  const onFinish = useCallback(
    async (values: ITemplateSaveValues) => {
      if (!block && !blocks) {
        return {}
      }

      if (values?.editorTemplateGroupId) {
        let result

        if (isMultiple && blocks) {
          result = await createTemplate({
            variables: {
              data: {
                projectId,
                name: values?.name,
                templateTagIds: values?.templateTagIds || [],
                blockUuids: blocks.map((b) => b.uuid),
                editorTemplateGroupId: values?.editorTemplateGroupId,
              },
            },
          })
        } else if (block) {
          result = await createTemplate({
            variables: {
              data: {
                projectId,
                name: values?.name,
                templateTagIds: values?.templateTagIds || [],
                blockUuids: [block?.uuid],
                editorTemplateGroupId: values?.editorTemplateGroupId,
              },
            },
          })
        }

        if (result?.data?.data.data) {
          sendTypedEvent('template.to_collection.saved', {
            templateId: result.data.data.data.id,
            templateName: values?.name || '',
            collectionId: values?.editorTemplateGroupId || '',
          })
          notify({ type: NotificationType.success, message: t('notify.templateSaved') })
          onClose?.(!!values)
        }
        if (result?.data?.data.errors?.length) {
          return Promise.resolve({ data: { errors: result?.data?.data.errors } })
        }
        return result?.data
      }
      return
    },
    [block, blocks, isMultiple, createTemplate, onClose, projectId],
  )

  return (
    <Form
      initialValues={initialFormValues}
      inputErrorValues={InputErrorValues}
      name='createTemplate'
      onChangeValues={onChangeValues}
      onReset={onClose}
      onSubmit={onFinish}
    >
      <Modal name='saveTemplate' title={t('modal.saveTemplate.title')}>
        <ModalBody sizeAutoCapable>
          <FormField config={nameField} />
          <FormField config={createdForField(collections)} />
          <FormField config={templateTagIds(templateTags, loading)} />
          <Typography indents={INFO_INDENTS} styleType='hint'>
            {t('modal.saveTemplate.templateTagIds.info')}
          </Typography>
        </ModalBody>
        <ModalFooter>
          <FormControls justify='right' />
        </ModalFooter>
      </Modal>
    </Form>
  )
}

export default TemplateSaveModal
